// La conversación de un contratado tal como la ve Pau (diseño §12.3, revisión S5a M5): nada técnico.
// Función pura: mensajes del motor de chat → elementos de la línea de tiempo. El pintado (y los
// textos, vía i18n) están en ConversacionOficina.tsx.
//
// Por qué una lista propia y no una variante de ConversationList: aquella lleva la maquinaria de /home
// (permisos fijados, resúmenes por turno, barra de progreso, modo desarrollador). Aquí se comparten
// sus piezas de DATOS (pairToolResults, groupBySubagent, scroll y paginación), no su pintado.
//
// Reglas:
// - Las herramientas seguidas se agrupan en UNA línea: cuántas veces miró, cambió o comprobó algo.
//   Ni nombres de fichero, ni comandos (hasta el nombre de un fichero es jerga para Pau).
// - Las del canal «oficina» son hitos: el contrato, una pregunta, un permiso, el informe. reportar_estado
//   solo pinta algo si cambia la fase.
// - Lo que la oficina manda al agente en nombre de Pau (el encargo, ⟦CONTRATO APROBADO⟧…) se pinta
//   como un hito corto, no como el texto largo que recibe el agente.
// - 13-10 (Pau: «me enviáis un montón de mensajes y pierdo el hilo»; medido: 6 de cada 10 eran narración de
//   pasos): por cada TURNO del agente solo sale como burbuja lo que le dice a Pau a propósito (mandar_a_pau);
//   el resto de lo que escribe se pliega en UNA línea «N pasos de trabajo» que se abre al tocarla. Red de
//   seguridad: si un turno acaba con texto DESPUÉS de lo último que mandó (o sin mandar nada), sale ese último
//   texto — también si Pau le escribió a mitad y contesta en texto. En los agentes nuevos (que usan mandar_a_pau
//   solo con texto), no en los turnos del despertador: sin novedad no le escribe nada (su aviso les dice que lo
//   que sea para Pau va por mandar_a_pau). Un turno empieza en cada
//   mensaje al agente (de Pau o de la oficina) y acaba en el fin de turno del motor (system/result) o cuando el
//   agente ya no está trabajando (se cayó o lo pararon: no llega fin de turno).
// - 14-10 (Pau: «dentro de los pasos de trabajo se me esconden mensajes que yo debería leer»; medido: ~200 en 3 días,
//   p. ej. contestaba largo, pedía un permiso y cerraba con «Te he mandado el permiso»): nunca se pliega un texto que
//   parece para Pau (`pareceParaPau`: largo, una pregunta, o su primera contestación a Pau antes de ponerse a
//   trabajar), salvo que en ese tramo ya le hablara con mandar_a_pau (lo repetiría). Sale en cuanto lo escribe, y la
//   narración de después va en otra línea de pasos (el orden se respeta).
import type { Message, StoredAttachment } from '../../../types';
import { groupBySubagent, pairToolResults, SUBAGENT_TOOL_NAMES } from '../../../lib/messages';
import { isAskUserQuestionPending, isPermissionPending } from '../../../lib/permissions/types';
import { encargoDelBrief, pareceParaPau, textoDePauConAviso } from '../compartido/mensajes';

export type Hito =
  | { clase: 'contratado' }
  | { clase: 'contrato_aprobado'; version: number }
  | { clase: 'cambios_pedidos'; texto: string }
  | { clase: 'respondiste'; texto: string }
  | { clase: 'cierre_pedido'; quien: 'pau' | 'agente' }
  | { clase: 'integrar_pedido' }
  | { clase: 'integrado' }
  | { clase: 'integrar_fallo' }
  | { clase: 'fase'; fase: string }
  | { clase: 'contrato_propuesto'; version: number }
  | { clase: 'pregunta'; texto: string }
  | { clase: 'permiso'; texto: string }
  // WS15-D8 — «integrar»: su nota de entrega (integra una parte y sigue); «cerrar»: su informe de cierre (se va).
  | { clase: 'informe'; modo: 'integrar' | 'cerrar' }
  | { clase: 'pensó'; segundos: number }
  | { clase: 'error' }
  // Un aviso de la oficina al agente (⟦OFICINA⟧: p. ej. un cambio en la forma de trabajar). No es de Pau.
  | { clase: 'aviso_oficina' }
  // 29-09 — cupo de uso: se le acabó en su servidor; lo pasamos a otro (o no había otro libre).
  | { clase: 'sin_cupo' }
  | { clase: 'trasladado' }
  // 29-09 — la respuesta de GitHub que la oficina le pasa al agente: es para él, no para Pau (se pliega).
  | { clase: 'consulta_github' }
  // WS15-D4 — la lista viva de agentes que la oficina le pasa al agente (ver_agentes): es para él (se pliega).
  | { clase: 'consulta_agentes' }
  // 29-09 — lo que pasó con un permiso que Pau respondió (⟦PERMISO …⟧): el texto largo es para el agente.
  | { clase: 'permiso_respondido'; resultado: 'aprobado' | 'hecho' | 'no_se_pudo' | 'denegado' }
  // Una confirmación del motor de Claude (no del canal «oficina»): el agente se queda esperando.
  | { clase: 'espera_confirmacion' };

export type Entrega = 'pendiente' | 'no_entregado' | 'enviado' | 'recibido' | null;

// `ids`: todos los mensajes que representa el elemento (lo que se pliega en él): así el historial del
// Expediente puede llevar a cualquiera de ellos.
export type Elemento =
  | { tipo: 'pau'; id: string; ids: string[]; texto: string; ts: string; adjuntos: StoredAttachment[]; entrega: Entrega }
  // `adjuntos`: lo que el agente le manda a Pau con `mandar_a_pau` (29-09); `noSePudieron`: nombres que no llegaron.
  // `paraPau`: lo mandó a propósito (mandar_a_pau): nunca se pliega.
  | { tipo: 'agente'; id: string; ids: string[]; texto: string; ts: string; adjuntos?: StoredAttachment[]; noSePudieron?: string[]; telegram?: 'enviado' | 'fallo'; paraPau?: boolean }
  // 13-10 — lo que el agente escribió mientras trabajaba en un turno, plegado («N pasos de trabajo»).
  | { tipo: 'pasos'; id: string; ids: string[]; ts: string; hasta: string; textos: Array<{ id: string; texto: string; ts: string }>; enCurso: boolean }
  | { tipo: 'trabajo'; id: string; ids: string[]; mirar: number; cambiar: number; comprobar: number; ayudantes: number; otros: number; fallos: number; enCurso: boolean }
  | { tipo: 'hito'; id: string; ids: string[]; ts: string; hito: Hito };

const ENTREGA: Record<string, Entrega> = { pending: 'pendiente', dispatched: 'enviado', acked: 'recibido' };

const MIRAR = new Set(['Read', 'Grep', 'Glob', 'WebFetch', 'WebSearch', 'LS']);
const CAMBIAR = new Set(['Edit', 'Write', 'MultiEdit', 'NotebookEdit']);
const COMPROBAR = new Set(['Bash', 'BashOutput', 'Monitor']);
const PREFIJO = 'mcp__oficina__';

const obj = (c: unknown): Record<string, unknown> => (c && typeof c === 'object' ? (c as Record<string, unknown>) : {});
const texto = (c: unknown): string => String(obj(c).text ?? '');

/**
 * Un mensaje que la oficina mandó al agente en nombre de Pau → su hito (o null si es de Pau).
 * 29-09: todo lo que empieza por ⟦…⟧ lo escribe la plataforma, nunca Pau (el servidor lo cuenta igual como
 * «orden»): una marca que aún no conocemos es un aviso de la oficina, jamás una burbuja de Pau con el texto técnico.
 */
function hitoDePlataforma(t: string, esPrimero: boolean): Hito | null {
  if (esPrimero && t.trimStart().startsWith('<entorno>')) return { clase: 'contratado' };
  const m = /^⟦([^⟧]+)⟧\s*([\s\S]*)$/.exec(t.trim());
  if (!m) return null;
  const [, marca, resto] = m as unknown as [string, string, string];
  if (marca.startsWith('CONTRATO APROBADO')) return { clase: 'contrato_aprobado', version: Number(/v(\d+)/.exec(marca)?.[1] ?? 1) };
  if (marca.startsWith('CAMBIOS AL CONTRATO')) return { clase: 'cambios_pedidos', texto: /Pau pide cambios: ([\s\S]*?)\n/.exec(`${resto}\n`)?.[1] ?? '' };
  if (marca.startsWith('DECISIÓN')) return { clase: 'respondiste', texto: /Pau responde: ([\s\S]*?)\.?$/.exec(resto)?.[1] ?? '' };
  // 30-09 — el ⟦CIERRE⟧ dice quién lo pidió; «— FALTA ALGO» y «— SIGUE» son avisos del cierre en curso, no otro cierre.
  if (marca === 'CIERRE') return { clase: 'cierre_pedido', quien: /^Pau ha pedido/.test(resto) ? 'pau' : 'agente' };
  if (marca === 'INTEGRADO') return { clase: 'integrado' };
  if (marca.startsWith('INTEGRAR — NO SE PUDO')) return { clase: 'integrar_fallo' };
  if (marca.startsWith('OFICINA · TRASLADO')) return { clase: 'trasladado' };
  if (marca.startsWith('OFICINA · GITHUB')) return { clase: 'consulta_github' };
  if (marca.startsWith('OFICINA · AGENTES')) return { clase: 'consulta_agentes' };
  if (marca.startsWith('PERMISO DENEGADO')) return { clase: 'permiso_respondido', resultado: 'denegado' };
  if (marca.startsWith('PERMISO APROBADO')) {
    const r = /NO SE PUDO/.test(marca) ? 'no_se_pudo' : /HECHO/.test(marca) ? 'hecho' : 'aprobado';
    return { clase: 'permiso_respondido', resultado: r };
  }
  return { clase: 'aviso_oficina' };
}

/**
 * WS15-D8 — en qué recorrido está el agente según las órdenes de la oficina que ha recibido: ⟦INTEGRAR⟧ (y su «— SIGUE»)
 * abre una integración (sigue después); ⟦CIERRE⟧ y su «— SIGUE» un cierre (se va; también el que llega a mitad de
 * integrar). Las devoluciones (FALTA ALGO, PONLO AL DÍA) no cambian nada: null.
 */
function recorridoDe(t: string): 'integrar' | 'cerrar' | null {
  const x = t.trimStart();
  if (/^⟦INTEGRAR(?:⟧| — SIGUE⟧)/.test(x)) return 'integrar';
  // Solo abren un cierre ⟦CIERRE⟧ y ⟦CIERRE — SIGUE⟧; una devolución (FALTA ALGO, PONLO AL DÍA) deja el recorrido como estaba.
  if (/^⟦CIERRE(?:⟧| — SIGUE⟧)/.test(x)) return 'cerrar';
  return null;
}

/** El despertador de la oficina: el único turno en que un agente nuevo que no usa mandar_a_pau no le escribe nada. */
const DESPERTADOR = /^⟦(?:OFICINA · DESPERTADOR|WAKE)/;

/** `trabajando`: si el agente sigue trabajando; si no, su último turno se da por acabado aunque no llegara su fin. */
export { encargoDelBrief };

export function lineaDeTiempo(mensajes: Message[], opciones: { trabajando?: boolean } = {}): Elemento[] {
  const { resultByToolUseId } = pairToolResults(mensajes);
  const { topLevel, childrenByParent } = groupBySubagent(mensajes);
  const out: Elemento[] = [];
  let trabajo: Extract<Elemento, { tipo: 'trabajo' }> | null = null;
  let faseAnterior: string | null = null;
  // WS15-D8 — sin orden de integrar, un informe es de cierre (como siempre).
  let recorrido: 'integrar' | 'cerrar' = 'cerrar';
  let primerUsuario = true;
  // Turnos (13-10): a qué turno pertenece cada texto del agente, cuáles terminaron y cuáles son del despertador.
  // El turno 0 es lo que hay antes del primer mensaje cargado (la página puede empezar a mitad de un turno).
  let turno = 0;
  const turnoDe = new Map<Elemento, number>();
  const terminados = new Set<number>();
  const despertador = new Set<number>();
  // Mensajes de Pau que llegaron a mitad de un turno (hora): lo que el agente escriba después le contesta.
  const pauAMitad = new Map<number, string[]>();
  // 14-10: la primera contestación del agente a un mensaje de Pau, si la escribe antes de ponerse a trabajar.
  const respuestaDirecta = new Set<Elemento>();
  let esperaRespuesta = false;
  // El texto suelto justo antes de un mandar_a_pau suele ser su borrador (sale dos veces); justo antes de una pregunta
  // de la oficina, la misma pregunta (ya sale en su tarjeta).
  const antesDeMandar = new Set<Elemento>();
  const antesDePregunta = new Set<Elemento>();
  let ultimoTexto: Elemento | null = null;
  // ¿Tiene el servidor nuevo (mandar_a_pau solo con texto)? Entonces habla con ella y no hace falta la red.
  let hablaConMandar = false;
  // 13-10 (Pau: «tiene bugs cuando envío mensajes»): la API del chat NO manda los mensajes de sistema (tampoco el fin
  // de turno, salvo con ?include=system). Sin ellos, un turno acaba cuando llega el siguiente mensaje al agente, o
  // cuando el agente deja de trabajar; y un mensaje de Pau siempre empieza turno. Si no, nunca acababa ninguno y sus
  // respuestas se quedaban plegadas en «pasos de trabajo».
  const conFinDeTurno = mensajes.some((m) => m.type === 'system');

  const cerrarTrabajo = () => {
    if (trabajo) out.push(trabajo);
    trabajo = null;
  };
  // Un mensaje que no pinta nada se pliega en el elemento en curso (o en el último pintado).
  const plegar = (id: string) => {
    const destino = (trabajo as Elemento | null) ?? out[out.length - 1];
    destino?.ids.push(id);
  };
  const sumar = (m: Message, nombre: string, toolUseId: string) => {
    if (!trabajo) trabajo = { tipo: 'trabajo', id: m.id, ids: [], mirar: 0, cambiar: 0, comprobar: 0, ayudantes: 0, otros: 0, fallos: 0, enCurso: false };
    trabajo.ids.push(m.id);
    if (MIRAR.has(nombre)) trabajo.mirar += 1;
    else if (CAMBIAR.has(nombre)) trabajo.cambiar += 1;
    else if (COMPROBAR.has(nombre)) trabajo.comprobar += 1;
    else if (SUBAGENT_TOOL_NAMES.has(nombre)) trabajo.ayudantes += 1;
    else trabajo.otros += 1;
    const r = resultByToolUseId.get(toolUseId);
    if (!r) trabajo.enCurso = true;
    else if (obj(r.content).is_error === true) trabajo.fallos += 1;
  };

  for (const m of topLevel) {
    switch (m.type) {
      case 'user': {
        cerrarTrabajo();
        const crudo = texto(m.content);
        // 13-10: lo que Pau escribe durante un cierre lleva delante un aviso para el agente: es su burbuja, sin él.
        const dePauConAviso = textoDePauConAviso(crudo) ?? undefined;
        const t = dePauConAviso ?? crudo;
        const hito = dePauConAviso ? null : hitoDePlataforma(t, primerUsuario);
        if (!dePauConAviso) recorrido = recorridoDe(t) ?? recorrido;
        primerUsuario = false;
        if (hito?.clase === 'contratado') {
          const encargo = encargoDelBrief(t);
          if (encargo) {
            turno += 1;
            out.push({ tipo: 'hito', id: m.id, ids: [m.id], ts: m.timestamp, hito });
            out.push({ tipo: 'pau', id: `${m.id}-encargo`, ids: [], texto: encargo, ts: m.timestamp, adjuntos: [], entrega: null });
            esperaRespuesta = true;
            break;
          }
        }
        // Si Pau escribe a mitad de un turno, el agente lo lee al acabar: el turno sigue (no se parte en dos).
        if (conFinDeTurno && !hito && !terminados.has(turno) && turno > 0 && hayTextoEn(turno)) {
          pauAMitad.set(turno, [...(pauAMitad.get(turno) ?? []), m.timestamp]);
          out.push({
            tipo: 'pau', id: m.id, ids: [m.id], texto: t, ts: m.timestamp, adjuntos: m.attachments ?? [],
            entrega:
              m.delivery?.state === 'pending' && ((m.delivery.attempts ?? 0) >= 4 || m.delivery.error === 'STALE_MAX_AGE')
                ? 'no_entregado'
                : ENTREGA[m.delivery?.state ?? ''] ?? null,
          });
          // No marca «primera contestación»: el agente lo lee al acabar el turno (lo cubre la red de abajo).
          ultimoTexto = null;
          break;
        }
        turno += 1;
        esperaRespuesta = !hito;
        ultimoTexto = null;
        if (hito && DESPERTADOR.test(t.trim())) despertador.add(turno);
        if (hito) out.push({ tipo: 'hito', id: m.id, ids: [m.id], ts: m.timestamp, hito });
        else out.push({
          tipo: 'pau', id: m.id, ids: [m.id], texto: t, ts: m.timestamp, adjuntos: m.attachments ?? [],
          // «No entregado» solo cuando el reintentador del servidor ya se rindió (4 intentos o marcado viejo,
          // DELIVERY_RETRY_*); antes aún puede llegar solo (revisiones R4/H4). El Expediente usa el
          // criterio exacto del servidor para ofrecer «Reenviar».
          entrega:
            m.delivery?.state === 'pending' && ((m.delivery.attempts ?? 0) >= 4 || m.delivery.error === 'STALE_MAX_AGE')
              ? 'no_entregado'
              : ENTREGA[m.delivery?.state ?? ''] ?? null,
        });
        break;
      }
      case 'assistant_text': {
        cerrarTrabajo();
        const t = texto(m.content).trim();
        // El aviso de Claude «You've hit your session limit…» no lo escribe el agente: es el cupo de la cuenta.
        const adjuntos = m.attachments ?? [];
        if (obj(m.content).model === '<synthetic>' && /\blimit\b/i.test(t)) out.push({ tipo: 'hito', id: m.id, ids: [m.id], ts: m.timestamp, hito: { clase: 'sin_cupo' } });
        else if (t || adjuntos.length) {
          // Lo que el agente le manda a Pau (mandar_a_pau) es un mensaje suyo con archivos, como los de Pau.
          const noSePudieron = obj(m.content).noSePudieron;
          const e: Elemento = {
            tipo: 'agente', id: m.id, ids: [m.id], texto: t, ts: m.timestamp,
            ...(adjuntos.length ? { adjuntos } : {}),
            ...(Array.isArray(noSePudieron) && noSePudieron.length ? { noSePudieron: noSePudieron.map(String) } : {}),
            ...(obj(m.content).telegram === 'enviado' || obj(m.content).telegram === 'fallo' ? { telegram: obj(m.content).telegram as 'enviado' | 'fallo' } : {}),
            ...(obj(m.content).origen === 'mandar_a_pau' ? { paraPau: true } : {}),
          };
          out.push(e);
          turnoDe.set(e, turno);
          if (esperaRespuesta) respuestaDirecta.add(e);
          esperaRespuesta = false;
          ultimoTexto = e.paraPau ? null : e;
        }
        else plegar(m.id);
        break;
      }
      case 'tool_use': {
        esperaRespuesta = false;
        const c = obj(m.content);
        const nombre = String(c.name ?? '');
        const input = obj(c.input);
        if (ultimoTexto && nombre === `${PREFIJO}mandar_a_pau`) antesDeMandar.add(ultimoTexto);
        if (ultimoTexto && nombre === `${PREFIJO}pedir_decision`) antesDePregunta.add(ultimoTexto);
        ultimoTexto = null;
        if (nombre.startsWith(PREFIJO)) {
          const h = nombre.slice(PREFIJO.length);
          // S10: la oficina rechaza lo mal formado (p. ej. un plan con un nombre de fichero) y el agente lo
          // repite corregido. El intento rechazado no le dice nada a Pau: no es un hito (sale el bueno).
          const resultado = resultByToolUseId.get(String(c.tool_use_id ?? ''));
          if (resultado && obj(resultado.content).is_error === true) {
            plegar(m.id);
            break;
          }
          if (h === 'mandar_a_pau' && !(Array.isArray(input.archivos) && input.archivos.length)) hablaConMandar = true;
          let hito: Hito | null = null;
          if (h === 'reportar_estado') {
            const fase = String(input.fase ?? '');
            if (fase && fase !== faseAnterior) hito = { clase: 'fase', fase };
            faseAnterior = fase || faseAnterior;
          } else if (h === 'proponer_contrato') hito = { clase: 'contrato_propuesto', version: Number(input.version ?? 1) };
          else if (h === 'pedir_decision') hito = { clase: 'pregunta', texto: String(input.pregunta ?? '') };
          // 30-09 — integrar no es un permiso de Pau (la oficina lo hace sola y el agente sigue).
          else if (h === 'pedir_permiso') {
            if (input.accion === 'integrar') recorrido = 'integrar';
            hito = input.accion === 'integrar' ? { clase: 'integrar_pedido' } : { clase: 'permiso', texto: String(input.que_hara ?? '') };
          } else if (h === 'terminar') recorrido = 'cerrar';
          else if (h === 'informe_de_cierre') hito = { clase: 'informe', modo: recorrido };
          if (hito) {
            cerrarTrabajo();
            out.push({ tipo: 'hito', id: m.id, ids: [m.id], ts: m.timestamp, hito });
          } else plegar(m.id);
          break;
        }
        if (nombre === 'TodoWrite') {
          plegar(m.id); // la lista de tareas vive en el Expediente
          break;
        }
        sumar(m, nombre, String(c.tool_use_id ?? ''));
        // Lo que hicieron sus ayudantes cuenta dentro de la misma línea.
        for (const hijo of childrenByParent.get(String(c.tool_use_id ?? '')) ?? []) {
          if (hijo.type !== 'tool_use') continue;
          const ch = obj(hijo.content);
          const n = String(ch.name ?? '');
          if (n && !n.startsWith(PREFIJO) && n !== 'TodoWrite') sumar(hijo, n, String(ch.tool_use_id ?? ''));
        }
        break;
      }
      case 'thinking_block': {
        const ms = Number(obj(m.content).durationMs ?? 0);
        // Pensar forma parte del trabajo: solo se enseña si fue largo y no hay herramientas abiertas.
        if (ms >= 10_000 && !trabajo) out.push({ tipo: 'hito', id: m.id, ids: [m.id], ts: m.timestamp, hito: { clase: 'pensó', segundos: Math.round(ms / 1000) } });
        else plegar(m.id);
        break;
      }
      case 'error':
        cerrarTrabajo();
        out.push({ tipo: 'hito', id: m.id, ids: [m.id], ts: m.timestamp, hito: { clase: 'error' } });
        break;
      case 'permission_request':
      case 'ask_user_question': {
        // Con el modo de los contratados no deberían llegar; si llega una, el agente se queda
        // esperando: se enseña (en azul) en vez de callarlo. Solo la pendiente, no su resolución.
        if (isPermissionPending(m.content) || isAskUserQuestionPending(m.content)) {
          cerrarTrabajo();
          out.push({ tipo: 'hito', id: m.id, ids: [m.id], ts: m.timestamp, hito: { clase: 'espera_confirmacion' } });
        } else plegar(m.id);
        break;
      }
      default:
        // system y tool_result (ya emparejado): no se pintan. El fin de turno cierra el turno en curso.
        if (m.type === 'system' && obj(m.content).subtype === 'result') terminados.add(turno);
        plegar(m.id);
        break;
    }
  }
  cerrarTrabajo();
  if (opciones.trabajando === false) terminados.add(turno);
  if (!conFinDeTurno) for (let n = 0; n < turno; n++) terminados.add(n);
  return plegarTurnos(out, turnoDe, { terminados, despertador, hablaConMandar, pauAMitad, respuestaDirecta, antesDeMandar, antesDePregunta });

  function hayTextoEn(n: number): boolean {
    for (const [e, k] of turnoDe) if (k === n && e.tipo === 'agente') return true;
    return false;
  }
}

/**
 * Por cada turno, solo queda a la vista lo que el agente le dice a Pau (ver la cabecera); el resto de sus textos
 * se pliega en una línea `pasos` donde estaba el primero. Copia: no toca la entrada.
 */
export function plegarTurnos(
  elementos: Elemento[],
  turnoDe: Map<Elemento, number>,
  o: { terminados: Set<number>; despertador: Set<number>; hablaConMandar: boolean; pauAMitad?: Map<number, string[]>; respuestaDirecta?: Set<Elemento>; antesDeMandar?: Set<Elemento>; antesDePregunta?: Set<Elemento> },
): Elemento[] {
  const porTurno = new Map<number, Array<Extract<Elemento, { tipo: 'agente' }>>>();
  for (const e of elementos) {
    const n = turnoDe.get(e);
    if (n === undefined || e.tipo !== 'agente') continue;
    const lista = porTurno.get(n) ?? [];
    lista.push(e);
    porTurno.set(n, lista);
  }
  const plegados = new Map<Elemento, number>();
  const enCurso = new Set<number>();
  for (const [n, textos] of porTurno) {
    const acabado = o.terminados.has(n);
    const ultimo = textos[textos.length - 1]!;
    const hablo = (e: Extract<Elemento, { tipo: 'agente' }>) => !!e.paraPau || !!e.adjuntos?.length;
    // Agente viejo: su último texto si va después de lo último que mandó (antes solo mandaba archivos). Agente nuevo
    // (13-10, visto en vivo: mandaba el mensaje y luego repetía lo mismo en texto → salía dos veces): su último texto
    // solo si en el turno no le habló, o si Pau le escribió a mitad DESPUÉS de lo que mandó (eso le contesta); y
    // nunca en el turno del despertador.
    const ultimoHablo = [...textos].reverse().find(hablo);
    const pauDespues = !!ultimoHablo && (o.pauAMitad?.get(n) ?? []).some((ts) => ts > ultimoHablo.ts);
    const red =
      !acabado || hablo(ultimo)
        ? undefined
        : !o.hablaConMandar
          ? ultimo
          : !o.despertador.has(n) && (!ultimoHablo || pauDespues)
            ? ultimo
            : undefined;
    // 14-10: lo que parece para Pau se ve, salvo que en ese tramo (desde el último mensaje de Pau) ya le hablara.
    const pau = o.pauAMitad?.get(n) ?? [];
    let hablaEn: string | null = null;
    for (const e of textos) {
      if (hablo(e)) {
        hablaEn = e.ts;
        continue;
      }
      if (e === red) continue;
      const yaLeHablo = hablaEn !== null && !pau.some((ts) => ts > hablaEn! && ts < e.ts);
      const opciones = { respuestaDirecta: o.respuestaDirecta?.has(e), sinPregunta: o.antesDePregunta?.has(e) };
      if (!yaLeHablo && !o.antesDeMandar?.has(e) && pareceParaPau(e.texto, opciones)) continue;
      plegados.set(e, n);
    }
    if (!acabado) enCurso.add(n);
  }
  if (plegados.size === 0) return elementos;
  const out: Elemento[] = [];
  const linea = new Map<number, Extract<Elemento, { tipo: 'pasos' }>>();
  for (const e of elementos) {
    const n = plegados.get(e);
    if (n === undefined || e.tipo !== 'agente') {
      out.push(e);
      // Lo que se plegue después de un mensaje suyo a la vista va en otra línea: el orden del chat se respeta.
      const k = turnoDe.get(e);
      if (e.tipo === 'agente' && k !== undefined) linea.delete(k);
      continue;
    }
    const paso = { id: e.id, texto: e.texto, ts: e.ts };
    const ya = linea.get(n);
    if (ya) {
      ya.textos.push(paso);
      ya.ids.push(...e.ids);
      ya.hasta = e.ts;
    } else {
      const nueva: Extract<Elemento, { tipo: 'pasos' }> = { tipo: 'pasos', id: `pasos-${e.id}`, ids: [...e.ids], ts: e.ts, hasta: e.ts, textos: [paso], enCurso: enCurso.has(n) };
      linea.set(n, nueva);
      out.push(nueva);
    }
  }
  return out;
}
